import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, XCircle, FileDown } from "lucide-react";
import Seo from "../components/Seo";
import { Pill, SectionHead, PrimaryLink, GhostLink, SpecDisclaimer, SpecTable, PlaceholderNote, CTABand, NetworkNote } from "../components/bits";
import { BluetoothFlow, CellularFlow } from "../components/diagrams";
import { PRODUCTS } from "../content/products";
import { SITE } from "../content/site";
import { trackEvent } from "../lib/api";

export default function Product({ productId }) {
  const p = PRODUCTS[productId];
  const other = PRODUCTS[p.crossSell];
  const params = useParams();

  useEffect(() => {
    trackEvent("product_view", { product: p.id });
  }, [p.id, params]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.fullName,
    description: p.proposition,
    brand: { "@type": "Brand", name: SITE.brand },
    category: p.category,
  };

  return (
    <>
      <Seo path={p.slug} title={p.fullName} description={p.proposition} jsonLd={jsonLd} />

      <section className="border-b border-border bg-brand-surface" data-testid={`product-hero-${p.id}`}>
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <Pill label={p.pill.label} tone={p.pill.tone} />
          <h1 className="mt-5 max-w-3xl text-4xl font-semibold tracking-tight text-brand-navy sm:text-5xl">{p.fullName}</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-slate md:text-lg">{p.proposition}</p>
          {p.networkStat && (
            <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-cyan-600/50 bg-cyan-50 px-4 py-1.5 font-mono text-xs font-semibold text-cyan-900" data-testid={`product-network-stat-${p.id}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" aria-hidden="true" />
              {p.networkStat}
            </p>
          )}
          <div className="mt-8 flex flex-wrap gap-4">
            <PrimaryLink to="/request-a-pilot" testid={`product-cta-pilot-${p.id}`}>Discuss Your Shipment</PrimaryLink>
            <GhostLink to="/compare-smart-labels" testid={`product-cta-compare-${p.id}`}>Compare with {other.name}</GhostLink>
          </div>
          <p className="mt-6 font-mono text-xs text-brand-slate">Content last reviewed: {p.lastReviewed}</p>
        </div>
      </section>

      {/* Product imagery */}
      {p.images && (
        <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-8" data-testid={`product-gallery-${p.id}`}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {p.images.map((img) => (
              <img
                key={img.url}
                src={img.url}
                alt={img.alt}
                loading="lazy"
                className="aspect-square w-full rounded-sm border border-border object-cover"
              />
            ))}
          </div>
        </section>
      )}

      {/* Best for + capabilities */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <SectionHead kicker="Best for" title="Where it fits" />
            <ul className="mt-8 space-y-3" data-testid={`product-bestfor-${p.id}`}>
              {p.bestFor.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-brand-slate">
                  <CheckCircle2 className={`mt-0.5 h-5 w-5 flex-shrink-0 ${p.id === "gnts1" ? "text-cyan-600" : "text-lime-600"}`} aria-hidden="true" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <SectionHead kicker="Capabilities" title="What it does" />
            <ul className="mt-8 grid gap-3 sm:grid-cols-2" data-testid={`product-capabilities-${p.id}`}>
              {p.capabilities.map((c) => (
                <li key={c} className="rounded-sm border border-border bg-white p-4 text-sm leading-relaxed text-brand-slate">{c}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Location transparency */}
      <section className="border-y border-border bg-brand-surface" data-testid={`product-location-${p.id}`}>
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
          <SectionHead kicker="Transparency" title={p.locationMethod.title} />
          <div className="mt-8 grid gap-10 lg:grid-cols-2">
            <div className="space-y-4">
              {p.locationMethod.body.map((para, i) => (
                <p key={i} className="text-sm leading-relaxed text-brand-slate">{para}</p>
              ))}
              {p.networkNote && <NetworkNote />}
            </div>
            {p.id === "gnts1" ? <BluetoothFlow /> : <CellularFlow />}
          </div>
        </div>
      </section>

      {/* Specs */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHead kicker="Specifications" title="The numbers — verified and pending" />
            <div className="mt-8"><SpecTable specs={p.specs} productId={p.id} /></div>
            <div className="mt-6 space-y-4">
              <SpecDisclaimer />
              <p className="text-sm text-brand-slate"><strong className="text-brand-navy">Activation:</strong> {p.activation}</p>
              <p className="text-sm text-brand-slate"><strong className="text-brand-navy">Operating life:</strong> {p.operatingLife}</p>
              <button
                disabled
                data-testid={`datasheet-download-${p.id}`}
                className="inline-flex cursor-not-allowed items-center gap-2 rounded-sm border border-dashed border-brand-slate/40 px-5 py-2.5 text-sm font-semibold text-brand-slate"
                title={p.datasheet.note}
              >
                <FileDown className="h-4 w-4" aria-hidden="true" /> {p.datasheet.label} — [asset pending]
              </button>
            </div>
          </div>
          <aside className="space-y-8">
            <div>
              <h3 className="text-sm font-semibold text-brand-navy">Certifications</h3>
              <ul className="mt-3 space-y-3" data-testid={`product-certs-${p.id}`}>
                {p.certifications.map((c) => (
                  <li key={c.name} className="rounded-sm border border-border p-3">
                    <p className="font-mono text-xs font-medium text-brand-navy">{c.name}</p>
                    <p className="mt-1 text-xs leading-relaxed text-brand-slate">{c.status}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-brand-navy">In GoAndTrack</h3>
              <ul className="mt-3 space-y-2" data-testid={`product-platform-${p.id}`}>
                {p.goandtrack.map((g) => (
                  <li key={g} className="flex items-start gap-2 text-sm text-brand-slate">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" aria-hidden="true" />{g}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* Limitations */}
      <section className="border-y border-border bg-brand-surface" data-testid={`product-limitations-${p.id}`}>
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
          <SectionHead kicker="Honest limits" title="What it doesn't do" sub="Buying the wrong label is expensive. These are the boundaries we'd rather you knew now." />
          <ul className="mt-10 grid gap-4 sm:grid-cols-2">
            {p.limitations.map((l) => (
              <li key={l} className="flex items-start gap-3 rounded-sm border border-border bg-white p-4 text-sm leading-relaxed text-brand-slate">
                <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" aria-hidden="true" />{l}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQs */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <SectionHead kicker="FAQs" title={`${p.name} questions, answered straight`} />
        <div className="mt-10 max-w-3xl divide-y divide-border border-y border-border" data-testid={`product-faqs-${p.id}`}>
          {p.faqs.map((f, i) => (
            <details key={f.q} className="group py-5" data-testid={`faq-${p.id}-${i}`}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold text-brand-navy hover:text-primary">
                {f.q}
                <span className="font-mono text-lg text-primary transition-transform duration-150 group-open:rotate-45" aria-hidden="true">+</span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-brand-slate">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Cross-sell */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-8">
        <div className="flex flex-col items-start justify-between gap-6 rounded-sm border border-border bg-brand-surface p-8 sm:flex-row sm:items-center" data-testid={`cross-sell-${p.id}`}>
          <div>
            <Pill label={other.pill.label} tone={other.pill.tone} />
            <h3 className="mt-3 text-xl font-semibold text-brand-navy">Need {p.id === "gnts1" ? "position anywhere, without readers" : "lower cost at high volume"}?</h3>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-brand-slate">{other.proposition}</p>
          </div>
          <Link to={other.slug} data-testid={`cross-sell-link-${p.id}`}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-sm border border-brand-navy/20 bg-white px-6 py-3 text-sm font-semibold text-brand-navy transition-colors duration-150 hover:border-primary hover:text-primary">
            Explore the {other.name} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <CTABand />
    </>
  );
}
